/**
 * Sharing screens of the founder's app: the choices, the words and the shape the Sharing page draws. Pure.
 * The rules live in ../api (src/domain/sharing.ts); the Listen words follow the privacy policy, "Sharing with Listen".
 */
import { ago } from './format'
import type { Grant, ShareScreen, ShareScreens, SharedWithMe } from './schemas'

export { PRIVACY_URL } from './coaching'

/** How long a share lasts. The API takes 1 to 366 days, or null for no end. */
export const SHARE_FOR = [
  { value: '7', days: 7, label: '7 days' },
  { value: '30', days: 30, label: '30 days' },
  { value: '90', days: 90, label: '90 days' },
  { value: 'open', days: null, label: 'No end' },
] as const
export type ShareFor = (typeof SHARE_FOR)[number]['value']
export const DEFAULT_SHARE_FOR: ShareFor = '30'

export function shareDays(value: ShareFor): number | null {
  const c = SHARE_FOR.find((x) => x.value === value)
  return c ? c.days : 30
}

/** The privacy policy's own words, so the switch promises exactly what the policy does. */
export const LISTEN_OFF = 'Off: Listen has no access to your computer, your code, your app or your data.'
export const LISTEN_ON = "On: Listen's investment team can view this screen. They can't reach your raw store data, your code or your computer."
export const LISTEN_NEVER = 'Listen uses shared screens to understand how consumer brands grow, never to build benchmarks or industry comparisons. Turn it off at any time.'

const DAY = 86_400_000

/** When a share ends, in a few words. */
export function endWords(g: Pick<Grant, 'expiresAt' | 'status'>, now = Date.now()): string {
  if (!g.expiresAt) return 'Until you stop it'
  const ms = Date.parse(g.expiresAt) - now
  if (g.status === 'expired' || ms <= 0) return `Ended ${ago(g.expiresAt, now)}`
  const days = Math.round(ms / DAY)
  return days < 1 ? 'Ends within a day' : days === 1 ? 'Ends tomorrow' : `Ends in ${days} days`
}

/** When a viewer's access ends, in their words. */
export function accessWords(expiresAt: string | null, now = Date.now()): string {
  return expiresAt ? endWords({ expiresAt, status: 'active' }, now) : 'No end date'
}

/** What's wrong with an email, as a person would want it said, or null. */
export function emailProblem(raw: string): string | null {
  const e = raw.trim()
  if (!e) return 'Type their email address.'
  if (e.length > 320 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return "That doesn't look like an email address."
  return null
}

/** One screen on the Sharing page: who sees it besides the team, and whether Listen does. */
export interface ScreenShares {
  screen: ShareScreen
  /** False for a screen the app no longer lists that still has grants: they can only be stopped. */
  listed: boolean
  people: Grant[]
  listen: Grant | null
}

/** Every listed screen in the app's order, then any unlisted screen that still has a grant. */
export function screenShares(screens: ShareScreen[], grants: Grant[]): ScreenShares[] {
  const of = (path: string) => grants.filter((g) => g.screen === path)
  const row = (screen: ShareScreen, listed: boolean): ScreenShares => {
    const mine = of(screen.path)
    return {
      screen,
      listed,
      people: mine.filter((g) => g.grantee.kind === 'person'),
      listen: mine.find((g) => g.grantee.kind === 'group' && g.grantee.group === 'listen' && g.status === 'active') ?? null,
    }
  }
  const listed = new Set(screens.map((s) => s.path))
  const gone = [...new Set(grants.map((g) => g.screen))].filter((p) => !listed.has(p))
  return [
    ...screens.map((s) => row(s, true)),
    ...gone.map((p) => row({ path: p, title: grants.find((g) => g.screen === p)?.title ?? p, description: '' }, false)),
  ]
}

/** The line above the list when it didn't come from the live app just now. Null when it did. */
export function sourceNote(s: Pick<ShareScreens, 'source' | 'reason' | 'checkedAt'>, now = Date.now()): string | null {
  if (s.source === 'live') return null
  const why = s.reason ? `${s.reason[0]!.toUpperCase()}${s.reason.slice(1)}.` : ''
  if (s.source === 'saved') return `${why} Showing the screens it listed ${ago(s.checkedAt, now)}.`.trim()
  return why || "Your app hasn't listed its screens yet."
}

/** The line under a person's email. */
export function personLine(g: Grant, now = Date.now()): string {
  const who = g.grantee.kind === 'person' && !g.grantee.signedUp ? "Hasn't signed in yet" : 'Signed in'
  return `${who} · ${endWords(g, now)}`
}

/** How many screens are shared with the person in all, for the viewer's page title. */
export const sharedCount = (s: SharedWithMe) => s.apps.reduce((n, a) => n + a.screens.length, 0)
